import styled from "styled-components";

import { insetDivider } from "../../../styles/insetDivider";
import { MOBILE } from "./mobile";

/* A row of a card with a switch or a button on the right (the Keepo connected, the security switches) */
export const SettingRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 20px;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  b {
    font-size: 0.875rem;
    color: var(--color-neutral-white);
  }

  span {
    overflow: hidden;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
    text-overflow: ellipsis;
  }

  .actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
  }

  /* A toggle, with a loader on its left while it saves */
  .control {
    display: flex;
    flex-shrink: 0;
    gap: 0.625rem;
    align-items: center;

    /* ToggleSwitch ships with a bottom margin and a text line box taller than its 24px track */
    > label {
      height: 24px;
      margin: 0;
      font-size: 0;
      line-height: 0;
    }
  }

  /* Phones: two buttons don't fit beside the text, they go under it */
  @media ${MOBILE} {
    &:has(.actions) {
      flex-direction: column;
      align-items: stretch;
    }

    .actions > * {
      flex: 1;
    }
  }
`;

/* Several SettingRows in one card, a thin line between them (as /settings' advanced configs) */
export const SettingGroup = styled.div`
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  > ${SettingRow} {
    background: transparent;
    border-radius: 0;
  }

  > ${SettingRow} + ${SettingRow} {
    ${insetDivider()}
  }
`;

/* Keepo's DNS or nameservers of your own */
export const Segmented = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  padding: 0.25rem;
  background: var(--color-neutral-dark);
  border-radius: 100px;

  button {
    padding: 0.5rem 0.75rem;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-150);
    border-radius: 100px;
    transition:
      color 0.15s,
      background-color 0.15s;

    &[aria-checked="true"] {
      color: var(--color-neutral-white);
      background: var(--color-neutral-base);
    }
  }
`;

/* The DNS records: type, name and value in a row per record (stacked on phones); Keepo's are locked */
export const Records = styled.ul`
  overflow: hidden;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  > li {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    padding: 0.875rem 20px;
  }

  > li + li {
    ${insetDivider()}
  }

  /* nothing on the list yet: the empty state's own row */
  > li.empty-state {
    display: block;
    padding: 0;
  }

  /* A warning on top of the list (e.g. the e-mail forwarding can't work right now) */
  > li.notice {
    display: block;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: #ffb066;
    background: rgba(255, 122, 0, 0.08);
  }

  .type {
    flex-shrink: 0;
    width: 52px;
    padding: 0.125rem 0;
    font-size: 0.6875rem;
    font-weight: 800;
    color: var(--color-neutral-white);
    text-align: center;
    background: var(--color-neutral-base);
    border-radius: 6px;
  }

  .record {
    display: grid;
    flex: 1;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.4fr);
    gap: 0.75rem;
    align-items: baseline;
    min-width: 0;

    @media ${MOBILE} {
      grid-template-columns: 1fr;
      gap: 0.125rem;
    }
  }

  .host {
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .value {
    overflow: hidden;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
    text-overflow: ellipsis;
    white-space: nowrap;

    small {
      margin-right: 0.375rem;
      font-family: Inter, sans-serif;
      font-weight: 700;
    }
  }

  .managed {
    display: inline-flex;
    flex-shrink: 0;
    gap: 0.25rem;
    align-items: center;
    font-size: 0.6875rem;
    font-weight: 700;
    color: var(--color-brand-plus);
  }

  .row-actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.25rem;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      color: var(--color-neutral-150);
      border-radius: 8px;
      transition:
        color 0.15s,
        background-color 0.15s;

      &:hover {
        color: var(--color-neutral-white);
        background: rgba(255, 255, 255, 0.06);
      }
    }
  }

  .empty {
    justify-content: center;
    padding: 1.5rem 20px;
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
    text-align: center;

    /* Couldn't load: the warning's color */
    &.error {
      color: #ffb066;
    }
  }

  /* Adding or editing a record: the form takes the row */
  .editing {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    background: rgba(255, 255, 255, 0.02);
  }

  .form-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }

  .form-error {
    font-size: 0.8125rem;
    color: var(--color-feedback-danger);
  }
`;
